import React from 'react';
import PropTypes from 'prop-types';
import { noop, isEqual, isInteger, isPlainObject, cloneDeep, inRange, isUndefined, filter, includes } from 'lodash';

import { DIRECTION } from '.';

/**
 * @type {function}
 * @desc A Higher-Order-Component to enhance functionality of `Component`
 * @param {object} Component - A React component
 * @example
 * import { multiStepManager, MultiStepManager } from 'modules/multi-step-manager';
 *
 * // create a component
 * const ComponentWithHOC = multiStepManager( CustomView );
 */

export class MultiStepManager extends React.Component {
        constructor( props ) {
			log.debug( 'MultiStepManager.constructor()' );

			super( props );

			// component state
			this.state = {
				activeStepIndex: isUndefined( props.activeStepIndex ) ? 0 : props.activeStepIndex,
				direction: DIRECTION.FORWARD,
				stepsPayload: []
			};

			// component data
			this.data = {
				totalSteps: props.totalSteps
			};

			// bind events and methods
			this.__bind();
        }

        // bind events and methods to the instance
    __bind() {
        this.handleMoveForward = this.handleMoveForward.bind( this ); // handler to move forward
        this.handleMoveBackward = this.handleMoveBackward.bind( this ); // handler to move backward
        this.changeStep = this.changeStep.bind( this ); // handler to change step
        this.getDirection = this.getDirection.bind( this ); // function that returns movement direction
		this.setStepsCount = this.setStepsCount.bind( this ); // function sets totak steps in component data
        this.getStepSpecificData = this.getStepSpecificData.bind( this );
        this.getStepsData = this.getStepsData.bind( this );
        this.handleExternalSkip = this.handleExternalSkip.bind( this );
    }

    /**************************************************************/

    /**
     * calling the skip method from outside, this is implemented for mobile view
     */
    handleExternalSkip() {
        log.debug( 'MultiStepManager.handleExternalSkip()' );

        this.handleMoveBackward();
    }

    // check if object is not a SyntheticEvent or anything else that instance of `Object`
    isPlainObject( object ) {
        return isPlainObject( object ); // (https://github.com/lodash/lodash/blob/master/isPlainObject.js) similar object.constructor === Object
    }


    // move to next step ( forward directtion )
    // it receives optional payload from the caller
    handleMoveForward( payload = {} ) {
        log.debug( 'MultiStepManager.handleMoveForward()' );

        // ignore if payload is not plain object
        if( ! this.isPlainObject( payload ) ) {
            return;
        }

        // new active step index
        const activeStepIndex = this.state.activeStepIndex;
        const newActiveStepIndex = this.state.activeStepIndex + 1;

        // moving direction
        const direction = DIRECTION.FORWARD;

		// get steps payload
        const stepsPayload = this.getStepsPayload( activeStepIndex, payload, this.props.wipeFutureStepDataOnBack, direction );

		// only update `activeStepIndex` when `newActiveStepIndex` is less than total steps
        this.setState(
            {
                activeStepIndex: ( newActiveStepIndex < this.data.totalSteps ) ? newActiveStepIndex : activeStepIndex,
                direction,
                stepsPayload,
            },
            () => {

                // call `onStepChange` method on props to denote step change event
                this.props.onStepChange( {
                    payload,
                    direction,
                    stepIndex: this.state.activeStepIndex
                } );
            }
        );
	}

    // move to prev step ( backward directtion )
    // it receives optional payload from the caller
    handleMoveBackward( payload = {} ) {
        log.debug( 'MultiStepManager.handleMoveBackward()' );

        // ignore if payload is not plain object
        if( ! this.isPlainObject( payload ) ) {
            return;
        }

        // new active step index
        const activeStepIndex = this.state.activeStepIndex;
        const newActiveStepIndex = this.state.activeStepIndex - 1;

        // moving direction
        const direction = DIRECTION.BACKWARD;

        // get steps payload
        const stepsPayload = this.getStepsPayload( activeStepIndex, payload, this.props.wipeFutureStepDataOnBack, direction );

        //  only update `activeStepIndex` when `newActiveStepIndex` is greater than or equal to 0
        this.setState(
            {
                activeStepIndex: ( 0 <= newActiveStepIndex ) ? newActiveStepIndex : activeStepIndex,
                direction,
                stepsPayload
            },
            () => {

                this.props.onStepChange( {
                    payload,
                    direction,
                    stepIndex: this.state.activeStepIndex
                } );
            }
        );
    }

    // change step
    changeStep( newActiveStepIndex = 0, payload = {} ) {
        log.debug( 'MultiStepManager.changeStep()' );

        // ignore if payload is not plain object
        if( ! this.isPlainObject( payload ) ) {
            return;
        }

        // check if `newActiveStepIndex` is in range (0, totalSteps)
        if( ! inRange( newActiveStepIndex, 0, this.data.totalSteps ) ) {
            return ;
        }

        // current active step index
        let _tempActiveStepIndex = this.state.activeStepIndex;

        // check if `newActiveStepIndex` is different than `activeStepIndex`
        // The multi-step manager can also be used to render multiple steps at the same time.
        // In that case, when the navigation to the same step is requested,
        // It is interpreted that the previous step has experienced a changed and wants its subsequenct steps to follow.
        // So if the `allowNavigationToSameStep` is true, then the processing should resume.
        if( _tempActiveStepIndex === newActiveStepIndex && ! this.props.allowNavigationToSameStep ) {
            return;
        }

        // direction is forward if `newActiveStepIndex` is greater than `activeStepIndex`
        let direction = newActiveStepIndex > _tempActiveStepIndex ? DIRECTION.FORWARD : DIRECTION.BACKWARD;

        // In case the same step is requested again, then the direction should be same.
        if( _tempActiveStepIndex === newActiveStepIndex ) {
            direction = DIRECTION.STAY_ON_SAME ;
        }

        // If the direction is either same or backward,
        // The temporary activeStepIndex should be set to one before the current one, so that future data can be wiped off.
        if ( DIRECTION.STAY_ON_SAME === direction || DIRECTION.BACKWARD === direction ) {
            _tempActiveStepIndex = newActiveStepIndex - 1;
        }

        // get steps payload
        const stepsPayload = this.getStepsPayload( _tempActiveStepIndex, payload, this.props.wipeFutureStepDataOnBack, direction );

        //  update component state
        this.setState(
            {
                activeStepIndex: newActiveStepIndex,
                direction,
                stepsPayload
            },
            () => {
                this.props.onStepChange( {
                    payload,
                    direction,
                    stepIndex: this.state.activeStepIndex
                } );
            }
        );
    }

    // create steps payload
	getStepsPayload( activeStepIndex, stepPayload, wipeFutureData = false, direction ) {
        log.debug( 'MultiStepManager.getStepsPayload()' );

        // if `stepPayload` is empty or unchanged, return stepsPayload from the state
        // if( isEmpty( stepPayload ) || isEqual( this.state.stepsPayload[ activeStepIndex ], stepPayload ) ) {
        //     return this.state.stepsPayload;
        // }

        // clone current `stepsPayload` from the state
        const stepsPayload = cloneDeep( this.state.stepsPayload );

        // remove steps data until current step
        const _stepsPayload = wipeFutureData ? stepsPayload.slice( 0, activeStepIndex ) : stepsPayload;

        // add new payload in the current step when not moving backward
        if( direction !== DIRECTION.BACKWARD ) {
            _stepsPayload[ activeStepIndex ] = stepPayload;
        }

        // return new `stepsPayload`
        return _stepsPayload;
	}

    // get step specific data in the state using `stepIndexs`
	getStepSpecificData( ...stepIndexs ) {
        log.debug( 'MultiStepManager.getStepSpecificData()' );

        // clone `stepsPayload`
        const stepsPayload = cloneDeep( this.state.stepsPayload );

        // if `stepIndexs` length is `1`, return as an object
        // else, return in an array
        if( 1 === stepIndexs.length ) {
            return stepsPayload[ stepIndexs ];
        } else {
            return filter( stepsPayload, ( _, index ) => {
                return includes( stepIndexs, index );
            } );
        }
    }

    // get all steps data
	getStepsData() {
        log.debug( 'MultiStepManager.getStepsData()' );

        return cloneDeep( this.state.stepsPayload );
    }

    // this function returns movement direction
    // this function is passed to `StepList` and `StepNavigation` to get `direction` value `dynamically` by calling this function
    // we can't pass `state.direction` as prop, as it will be cached by the rendered child of the `StepList` component
    getDirection() {
        return this.state.direction;
    }

    // this function sets set count, called by inner `StepList` components
    setStepsCount( count ) {
        this.data.totalSteps = count;
    }

    /**************************************************************/

    // when component will receive new props, update activeStepIndex
	UNSAFE_componentWillReceiveProps( nextProps ) {
        log.debug( 'MultiStepManager.componentWillReceiveProps()' );

		if( this.props.subscribeActiveStepIndex && isInteger( nextProps.activeStepIndex ) && !isEqual( this.state.activeStepIndex, nextProps.activeStepIndex ) ) {
			this.setState( {
                activeStepIndex: nextProps.activeStepIndex,
                direction: nextProps.activeStepIndex > this.state.activeStepIndex ? DIRECTION.FORWARD : DIRECTION.BACKWARD,
            } );
		}
    }

    // since chidren of this component will be `StepsNavigation` or `StepList` component,
    // Using React.Children API, modify child element selectively and provide necessary props
    render() {
        log.debug( 'MultiStepManager.render()' );

        // get context provider
        const { Provider } = this.props.context;

        // context value
        const contextValue = {
            activeStepIndex: this.state.activeStepIndex, // active step index
            moveBackward: this.handleMoveBackward,  // handler to move backward
            moveForward: this.handleMoveForward,    // handler to move forward
            changeStep: this.changeStep,    // handler to change step
            canMoveForward: this.state.activeStepIndex < this.data.totalSteps - 1,  // can move forward boolean value
			canMoveBackward: 0 < this.state.activeStepIndex,  // can move backward boolean value
            getDirection: this.getDirection,  // get movement direction
            getStepSpecificData: this.getStepSpecificData, // get step specific data
            getStepsData: this.getStepsData, // get all steps data
            setStepsCount: this.setStepsCount, // set total steps count
            showMultiSteps: this.props.showMultiSteps
        };

        return (
            <Provider value={ contextValue }>
                { this.props.children }
            </Provider>
        );
    }
}

// set display name
MultiStepManager.displayName = 'MultiStepManager';

// default props
MultiStepManager.defaultProps = {
	onStepChange: noop,
	wipeFutureStepDataOnBack: true,
    allowNavigationToSameStep: false,
    subscribeActiveStepIndex: false,
};

// prop types
MultiStepManager.propTypes = {
	context: PropTypes.any.isRequired,
	activeStepIndex: PropTypes.number,
	onStepChange: PropTypes.func,
	wipeFutureStepDataOnBack: PropTypes.bool
};
